/* ============================================================================
   Agreement display helpers (formatters only).
   All risk/governance DERIVATIONS now come server-computed from
   GET /api/courierPartner/parent/agreement/timeline/{parentId} items:
     {agreement, expired, expiresInDays, reviewDueOn, stage1AgingDays,
      backdated, overlappingIds}
   and from GET /api/courierPartner/parent/dashboard (KPI strip / risk tiles).
   This file only formats those flags into chip text + small label helpers.
   ========================================================================== */
import {AGREEMENT_TYPE_LABEL, STAGE_NUM} from '../supplyMeta'

/* display constants (mirror the server SLAs — used in tile copy only) */
export const EXPIRY_SOON_DAYS = 90
export const STAGE1_SLA_DAYS = 30

/* ── stage / status predicates (pure field reads on the server doc) ─────── */
export const stageNum = (a) => STAGE_NUM[a.stage] || 1
export const isSignedGoverning = (a) => a.status === 'ACTIVE' && a.stage === 'SIGNED'
export const isOpenStage1 = (a) => a.status === 'ACTIVE' && a.stage === 'EMAIL_RECEIVED'
export const isPendingApproval = (a) => a.status === 'PENDING_APPROVAL'
export const isSuperseded = (a) => a.status === 'SUPERSEDED'

/* ── type labels ─────────────────────────────────────────────────────────── */
/* short label — overview mini-tables: MSA | Addendum #N | Operational Addendum */
export const agrTypeShort = (a) => {
  if (a.type === 'MSA') return 'MSA'
  if (a.type === 'COMMERCIAL_ADDENDUM') return 'Addendum' + (a.addendumNo != null ? ' #' + a.addendumNo : '')
  return AGREEMENT_TYPE_LABEL[a.type] || a.type || '—'
}

/* full label — preview / timeline header */
export const agrTypeFull = (a) => {
  if (a.type === 'MSA') return 'MSA'
  const base = AGREEMENT_TYPE_LABEL[a.type] || a.type || '—'
  return a.addendumNo != null ? base + ' #' + a.addendumNo : base
}

/* ── risk chips from the SERVER-COMPUTED timeline flags ──────────────────── */
/* item: {agreement, expired, expiresInDays, reviewDueOn, stage1AgingDays,
          backdated, overlappingIds} → [{cls|'blue', text}] */
export function riskChips(item, fmtDate) {
  const a = item.agreement || item
  const chips = []
  if (item.expired) chips.push({cls: 'mp-red', text: 'Expired'})
  else if (item.expiresInDays != null) chips.push({cls: 'mp-amber', text: 'Expires in ' + item.expiresInDays + 'd'})
  if (item.reviewDueOn) chips.push({cls: 'mp-amber', text: 'Review due ' + (fmtDate ? fmtDate(item.reviewDueOn) : item.reviewDueOn)})
  if (item.stage1AgingDays != null)
    chips.push({cls: 'mp-amber', text: 'Open ' + item.stage1AgingDays + 'd · unsigned'})
  if (item.backdated) chips.push({cls: 'mp-grey', text: 'Backdated'})
  if (item.overlappingIds && item.overlappingIds.length)
    chips.push({
      cls: 'mp-red',
      text: 'Overlap',
      tip:
        'Overlaps ' + item.overlappingIds.length + ' other governing agreement' +
        (item.overlappingIds.length > 1 ? 's' : '') +
        ' of the same type on shared codes. Allowed but ambiguous — use “Supersede” on the older one to resolve.',
    })
  if (isSuperseded(a)) chips.push({cls: 'mp-red', text: 'Superseded'})
  if (a.status === 'REJECTED') chips.push({cls: 'mp-red', text: 'Rejected'})
  if (isPendingApproval(a)) chips.push({blue: true, text: 'Pending approval'})
  return chips
}

/* ── per-code agreement badge from live agreements ───────────────────────── */
/* courierId: courier_partners._id ; agrs: agreement docs for the parent */
export function codeBadgeFor(courierId, agrs) {
  const inScope = (a) => a.appliesToAll || (a.linkedCpIds || []).indexOf(courierId) >= 0
  const signed = (agrs || []).filter((a) => isSignedGoverning(a) && inScope(a))
  const pending = (agrs || []).filter((a) => isOpenStage1(a) && inScope(a))
  if (signed.length && pending.length)
    return {key: 'addendum', tip: signed.length + ' signed · ' + pending.length + ' pending signing'}
  if (signed.length) return {key: 'signed', tip: signed.length + ' signed agreement(s) govern this code'}
  if (pending.length) return {key: 'pending', tip: pending.length + ' agreement(s) pending signing'}
  return {key: 'norecord', tip: 'No agreement covers this code'}
}

/* deterministic avatar colour per partner name (backend has no colour field) */
const PALETTE = ['#6366F1', '#1976D2', '#0F766E', '#2E7D32', '#F57C00', '#6A1B9A', '#00897B', '#B45309', '#1565C0', '#8D6E63']
export const colorFor = (name) => {
  let h = 0
  const s = String(name || '')
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return PALETTE[Math.abs(h) % PALETTE.length]
}

/* account-code display label for a courier doc returned by codesByParent() */
export const codeLabel = (c) => c.cpAccountCode || c.prozoId || c.name || c.id
